import FastImage, { Source } from '@d11/react-native-fast-image';
import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../../app/hooks';
import {
  COLORS,
  PODCAST_HERO_CARD_HEIGHT,
  PODCAST_HERO_CARD_WIDTH,
} from '../../constants';
import {
  selectDefaultCover,
  selectDivRadius,
} from '../../features/appconfig/store/selectors';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import { formatFromNow } from '../../localization/datetime';
import { PodcastEpisodeModel } from '../../models';
import CustomText from '../CustomText';
import { PlayPauseButton } from '../PlayPauseButton';

interface PodcastHeroCardProps {
  episode: PodcastEpisodeModel;
  onPress?: () => void;
}

export type { PodcastHeroCardProps };

export const PodcastHeroCard: React.FC<PodcastHeroCardProps> = ({ episode, onPress }) => {
  const divRadius = useAppSelector(selectDivRadius);
  const defaultCover = useAppSelector(selectDefaultCover) as Source | number;
  const titleFontSize = useAdjustedFontSize(mvs(16));
  const infoFontSize = useAdjustedFontSize(mvs(12));

  const backgroundSource: Source | number =
    episode.image || episode.podcastLogo
      ? { uri: episode.image || episode.podcastLogo }
      : defaultCover;
  const thumbnailSource: Source | number = episode.podcastLogo
    ? { uri: episode.podcastLogo }
    : defaultCover;

  return (
    <TouchableOpacity style={styles.container} onPress={onPress} activeOpacity={0.95}>
      <View style={[styles.card, { borderRadius: mvs(divRadius) }]}>
        {/* background image */}
        <View style={styles.backgroundImage}>
          <FastImage
            source={backgroundSource}
            style={StyleSheet.absoluteFill}
            resizeMode={FastImage.resizeMode.cover}
          />
          {/* podcast thumbnail with border */}
          <View style={[styles.thumbnailContainer]}>
            <FastImage
              source={thumbnailSource}
              style={styles.thumbnail}
              resizeMode={FastImage.resizeMode.cover}
            />
          </View>

          {/* play button */}
          <View style={styles.playButtonContainer}>
            <PlayPauseButton size={45} iconSize={20} podcastEpisode={episode} />
          </View>

          {/* podcast name */}
          <View style={styles.podcastNameContainer}>
            <CustomText
              style={[styles.podcastName, { fontSize: infoFontSize }]}
              weight="Bold"
              numberOfLines={1}
            >
              {episode.podcastTitle}
            </CustomText>
          </View>

          {/* episode information */}
          <View style={styles.episodeInfo}>
            <CustomText
              style={[styles.publishedAt, { fontSize: infoFontSize }]}
              weight="Regular"
              numberOfLines={1}
            >
              {formatFromNow(new Date(episode.date), { addSuffix: true })}
            </CustomText>
            <CustomText
              style={[styles.episodeTitle, { fontSize: titleFontSize }]}
              weight="Regular"
              numberOfLines={2}
            >
              {episode.title}
            </CustomText>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

// getitemlayout function for horizontal flatlist performance
export const getPodcastHeroCardItemLayout = (_: unknown, index: number) => ({
  length: mvs(PODCAST_HERO_CARD_WIDTH), // width of each card
  offset: mvs(PODCAST_HERO_CARD_WIDTH + 16) * index, // width + gap between items
  index,
});

const styles = StyleSheet.create({
  container: {
    height: mvs(PODCAST_HERO_CARD_HEIGHT),
    width: mvs(PODCAST_HERO_CARD_WIDTH),
  },
  card: {
    overflow: 'hidden',
    height: '100%',
    borderRadius: mvs(20),
    backgroundColor: COLORS.WHITE,
  },
  backgroundImage: {
    height: mvs(200),
    position: 'relative',
  },
  thumbnailContainer: {
    position: 'absolute',
    left: mvs(15),
    top: mvs(175),
    width: mvs(50),
    height: mvs(50),
    borderRadius: mvs(12),
    borderWidth: 4,
    borderColor: COLORS.WHITE,
    overflow: 'hidden',
  },
  thumbnail: {
    width: '100%',
    height: '100%',
  },
  playButtonContainer: {
    position: 'absolute',
    right: mvs(15),
    top: mvs(145),
  },
  podcastNameContainer: {
    position: 'absolute',
    left: mvs(75),
    right: mvs(15),
    top: mvs(204),
  },
  podcastName: {
    color: '#252525',
    lineHeight: mvs(20),
  },
  episodeInfo: {
    position: 'absolute',
    top: mvs(232),
    paddingHorizontal: mvs(16),
  },
  publishedAt: {
    color: '#9c9c9c',
    marginBottom: mvs(4),
  },
  episodeTitle: {
    color: '#474747',
    lineHeight: mvs(20),
  },
});

export default PodcastHeroCard;
